import { Button, Card, Container, Table, TableBody, TableContainer } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import { LoadingScreen } from 'src/components/loading-screen';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import { TableNoData, TablePaginationCustom, useTable, TableHeadCustom } from 'src/components/table';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { RoleBasedGuard } from 'src/auth/guard';
import { useAuthContext } from 'src/auth/hooks';
import { useGetAllTourPlanMutation } from 'src/services/affordable/tour-plan/tour-plan-api';
import { RouterLink } from 'src/routes/components';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import ErrorView from 'src/sections/error/error-view';
import TourPlanTableRow from '../tour-plan-table-row';
import AffordableTableToolbar from '../affordable-table-toolbar';
import TourPlanQuickView from '../tour-plan-quick-view';

const TABLE_HEAD = [
    { id: 'days', label: 'Tour Plan Days' },
    { id: 'inquiry', label: 'Tour Inquiry'},
    { id: 'action', label: ''}
];

const defaultFilters = {
    name: '',
    ref: ''
};

export default function TourPlanView() {
    const settings = useSettingsContext();

    const router = useRouter();

    const table = useTable({ defaultOrderBy: 'name' });

    const [tableData, setTableData] = useState([]);
    const [totalElements, setTotalElements] = useState(0);

    const [filters, setFilters] = useState(defaultFilters);
    const [searchTimer, setSearchTimer] = useState(null);
    const [searchKey, setSearchKey] = useState(null);
    const [isShow, setIsShow] = useState(false);

    const { isUserRoleHasAccess } = useAuthContext();

    const [getAllTourPlans, { isLoading, isSuccess, data, error }] = useGetAllTourPlanMutation();

    const canReset =
        !!filters.name ||
        !!filters.ref;

    const notFound = (!tableData.length && canReset) || !tableData.length;

    const quickView = useBoolean();
    const [viewData, setViewData] = useState(null);

    const handleResetFilters = useCallback(() => {
        setSearchKey(null);
        setFilters(defaultFilters);
    }, []);

    const handleFilters = useCallback((name, value) => {
        table.onResetPage();

        setSearchKey(value);

        if (searchTimer) {
            clearTimeout(searchTimer);
        }

        if (!value) {
            handleResetFilters();
        }

        const newSearchTimer = setTimeout(() => {
            getAllTourPlans({ page: table.page, size: table.rowsPerPage, key: value });
        }, 1000);

        setSearchTimer(newSearchTimer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[handleResetFilters, searchTimer, table]);

    useEffect(() => {
        getAllTourPlans({ page: table.page, size: table.rowsPerPage, key: searchKey });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAllTourPlans, table.page, table.rowsPerPage]);

    const handleViewRow = useCallback(
        (row) => {
            const dataObject = {
                tourPlan: row
            }
            router.pushData(paths.dashboard.affordable.viewTourPlan(row.id), dataObject);
        },
        [router]
    );

    const handleQuickViewRow = useCallback((row) => {
        setViewData(row);
    },[]);

    useEffect(() => {
        if(viewData && !quickView.value){
            quickView.onTrue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewData]);

    useEffect(() => {
        if(!quickView.value){
            setViewData(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quickView]);

    const handleEditRow = useCallback(
        (row) => {
            const dataObject = {
                tourPlan: row
            }
            const searchParams = new URLSearchParams({
                isEdit: true
            }).toString();
            const href = `${paths.dashboard.affordable.editTourPlan(row.id)}?${searchParams}`;
            router.pushData(href, dataObject);
        },
        [router]
    );

    useEffect(() => {
        if (isSuccess) {
            setTableData(data.content)
            setTotalElements(data.totalElements);
            if (searchKey) {
                setIsShow(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess, data]);

    let tourPlanContent;
    if (isLoading) {
        tourPlanContent = <LoadingScreen />
    }
    else if (error) {
        tourPlanContent = <ErrorView error={error?.data} showTryAgain={false}/>
    }
    else if (isSuccess) {
        tourPlanContent = (
            <Card>
                <AffordableTableToolbar
                    value={searchKey}
                    onFilters={handleFilters}
                />

                <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                    <Scrollbar>
                        <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 800 }} stickyHeader>
                            <TableHeadCustom
                                order={table.order}
                                orderBy={table.orderBy}
                                headLabel={TABLE_HEAD}
                                rowCount={tableData.length}
                                numSelected={table.selected.length}
                                onSort={table.onSort}
                                onSelectAllRows={(checked) =>
                                table.onSelectAllRows(
                                checked,
                                tableData.map((row) => row.id)
                                )}
                            />

                            <TableBody>
                                {tableData
                                    .map((row) => (
                                        <TourPlanTableRow
                                            key={row.id}
                                            row={row}
                                            onViewRow={() => handleQuickViewRow(row)}
                                            onEditRow={() => handleEditRow(row)}
                                        />
                                    ))}
                                <TableNoData notFound={notFound} />
                            </TableBody>
                        </Table>
                    </Scrollbar>
                </TableContainer>
                {tableData.length > 0 &&
                    <TablePaginationCustom
                        count={totalElements}
                        page={table.page}
                        rowsPerPage={table.rowsPerPage}
                        onPageChange={table.onChangePage}
                        onRowsPerPageChange={table.onChangeRowsPerPage}
                        dense={table.dense}
                        onChangeDense={table.onChangeDense}
                    />}
            </Card>
        );
    }

    return (
        <Container maxWidth={settings.themeStretch ? false : 'lg'}>
            <CustomBreadcrumbs
                heading="Tour Plan"
                links={[
                    {
                        name: 'Dashboard',
                        href: paths.dashboard.driverReview.root,
                    },
                    {
                        name: 'Tour Plan',
                        href: paths.dashboard.payment.paymentCollector,
                    },
                ]}
                action={
                    isUserRoleHasAccess(3, 2)
                    ?
                    <Button
                        component={RouterLink}
                        href={paths.dashboard.affordable.createTourPlan}
                        variant="contained"
                        startIcon={<Iconify icon="mingcute:add-line" />}
                    >
                        Add Tour Plan
                    </Button>
                    :
                    <></>
                }
                sx={{
                    mb: { xs: 3, md: 5 },
                }}
            />

            <RoleBasedGuard hasContent mainMenuOrder={3} subMenuOrder={2} sx={{ py: 10 }}>
                {tourPlanContent}

                <TourPlanQuickView data={viewData} open={quickView.value} onClose={quickView.onFalse} />
            </RoleBasedGuard>
        </Container>
    )
}
